import { useContext, useMemo } from 'react'
import type { RateView } from 'components/OfferPage/Property/singlePropertyUtils'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { useAppSelector } from 'hooks/reduxHooks'
import { min } from 'lib/array/arrayUtils'
import { initialRooms } from 'lib/offer/occupancyUtils'
import { addFlightPrice, onlyNonZeroFees } from 'lib/offer/pricing'
import {
  getPropertyCombinedRatesByRoom,
  getPropertyMappedBedbankOffer,
} from 'selectors/offerPage/offerPageSelectors'

type Return = [
  pricing: App.PricingWithValue | undefined,
  memberPricing: App.PricingWithValue | undefined,
  pricingWithFlights: App.PricingWithValue | undefined,
  memberPricingWithFlights: App.PricingWithValue | undefined,
  rateView: RateView | undefined,
]

// Unfortunately, `EmptyArray` isn't compatible with `Return`.
const emptyReturn: Return = [undefined, undefined, undefined, undefined, undefined]

/**
 * @deprecated This hook (for SPP offers) is being refactored out into selectors. If you _need_ to
 * use this hook, speak to Xiao or Robert.
 *
 * Unlike `<PropertyRoomOption/>`, this hook does _not_ account for GHA-highlighted rates or the
 * `accom_use_property_page_rates_endpoint` feature flag.
 */
function useCheapestPropertyPricing(): Return {
  const offerPageState = useContext(OfferPageStateContext)
  const {
    airport: flightOrigin,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    rooms = initialRooms,
  } = offerPageState
  const bedbankOffer = useAppSelector(state => getPropertyMappedBedbankOffer(offerPageState, state))
  const combinedRatesByRoom = useAppSelector(state =>
    getPropertyCombinedRatesByRoom(offerPageState, state))

  // Handle Bedbank
  const bedbankFlightPrice = useBedbankFlightPrice(bedbankOffer, {
    rooms,
    startDate: checkIn,
    endDate: checkOut,
    originAirportCode: flightOrigin?.code,
  })

  // Combine
  const cheapestRoomRates = useMemo(() => combinedRatesByRoom.flatMap(({ displayRates }) => {
    // We're ignoring the implicit sorting of `displayRates` in case the first element is the
    // GHA-highlighted rate.
    return min(displayRates, ({ price }) => price) ?? []
  }), [combinedRatesByRoom])
  return useMemo(() => {
    const cheapestRate = min(cheapestRoomRates, ({ price }) => price)
    // TODO: should never be property
    if (!cheapestRate || cheapestRate.type === 'property') return emptyReturn

    if (cheapestRate.type === 'bedbank') {
      const { totals, value, discount, memberDiscount } = cheapestRate.bedbankRate.rate
      const fees = onlyNonZeroFees([
        { type: 'taxesAndFees', amount: totals.taxesAndFees },
        { type: 'property', amount: totals.propertyFees ?? 0 },
      ])
      const pricing = {
        price: totals.total,
        fees,
        saleUnit: 'total',
        value,
        discountPercent: discount,
      }
      const memberPricing = (totals.memberTotal && totals.memberTotal > 0) ? {
        price: totals.memberTotal,
        fees,
        saleUnit: 'total',
        value,
        discountPercent: memberDiscount,
      } : undefined
      const pricingWithFlights = bedbankFlightPrice ?
          addFlightPrice(pricing, bedbankFlightPrice) :
        undefined
      const memberPricingWithFlights = (memberPricing && bedbankFlightPrice) ?
          addFlightPrice(memberPricing, bedbankFlightPrice) :
        undefined
      return [pricing, memberPricing, pricingWithFlights, memberPricingWithFlights, cheapestRate]
    }

    const {
      pricing,
      memberPricing,
      pricingWithFlights,
      memberPricingWithFlights,
    } = cheapestRate.lpcRate
    return [pricing, memberPricing, pricingWithFlights, memberPricingWithFlights, cheapestRate]
  }, [cheapestRoomRates, bedbankFlightPrice])
}

export default useCheapestPropertyPricing
